import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import GroupUtil from "../../utilities/group-util";
import { Utils } from "../../utilities/utils";
import { useTranslation } from 'react-i18next';
import { ListOptions } from "../../models/interfaces/list-options";
import { DateTime } from "luxon";
import { Group } from "../../models/interfaces/group";
import { Tag } from "../../models/interfaces/tag";
import { DetailFilterState } from "../../models/interfaces/detail-filter-state";
import { DetailUserRow } from "../../models/interfaces/detail-user-row";
import { Button } from "../button/button";
import ReportDataUtil from "../../utilities/report-data-util";
import Select from "react-select";
import { HEALTH_SEVERITY_ORDER, MskHealthRiskOptions } from "../../models/msk-health-risk";
import { FocusGroupOptions } from "../../models/focus-group";
import { ReportType } from "../../models/enumerations/report-type";
import ReportDynamicTable, { TableColumn } from "./report-dynamic-table";
import { PainFilterOption, PainFilterOptionToTranslationKey } from "../../models/enumerations/pain-filter-option";
import { ReportDynamicDetailData } from "../../models/interfaces/reports/report-dynamic-detail-data";
import ReportFilterMenu from "../report-filter-menu/report-filter-menu";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import { Loader } from "../loader/loader";

const COMPONENT_CLASS = "c-report-detail-dynamic";

const DEFAULT_FILTERS: DetailFilterState = {
    groupIds: [],
    tagIds: [],
    healthCategories: [],
    focusAreas: [],
    inPain: PainFilterOption.ALL,
};



// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportDetailDynamic: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const { state } = useAuthState();
    const painOptions = useMemo(() =>
        Object.values(PainFilterOption).map(value => ({
            value,
            label: t(PainFilterOptionToTranslationKey[value])
        })),
        [t]);
    const reportMonthOptions = useMemo<ListOptions[]>(() => {
        const options: ListOptions[] = [
            {
                value: DateTime.now().startOf("month").toISODate()!,
                label: t('reports.currentMonth')
            }
        ];

        if (!state.organization?.created) {
            return options;
        }

        let monthDate = DateTime.now().minus({ month: 1 });

        const orgCreatedDate = DateTime
            .fromJSDate(new Date(state.organization.created.seconds * 1000))
            .startOf('month');

        const monthDifference = DateTime.now()
            .startOf('month')
            .diff(orgCreatedDate, 'months')
            .months;

        while (options.length <= monthDifference) {
            options.push({
                value: monthDate.startOf("month").toISODate()!,
                label: monthDate
                    .setLocale(i18n.language)
                    .toFormat("MMMM yyyy"),
            });

            monthDate = monthDate.minus({ month: 1 });
        }

        return options;

    }, [t, state.organization.created, i18n.language]);
    const [reportData, setReportData] = useState<ReportDynamicDetailData | null>(null);
    const [filteredReportData, setFilteredReportData] = useState<ReportDynamicDetailData | null>(null);
    const [filters, setFilters] = useState<DetailFilterState>(DEFAULT_FILTERS);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [selectedReportMonth, setSelectedReportMonth] = useState<string>(
        reportMonthOptions[0]?.value
    );
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);

    const columns = useMemo<TableColumn<DetailUserRow>[]>(() => [
        {
            key: "name",
            header: t('reports.detail.table.name'),
            sortable: true,
            width: 180,
            fontWeight: 400,
            render: row => (
                <a
                    href={`${window.location.origin}/users/${row.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {row.name}
                </a>
            )
        },
        {
            key: "age",
            header: t('reports.detail.table.age'),
            sortable: true,
            render: row => Utils.getAge(row.dob),
            sortAccessor: row => Utils.getAge(row.dob),
            width: 60,
        },
        {
            key: "groupName",
            header: t('reports.detail.table.group'),
            sortable: true,
            width: 160,
        },
        {
            key: "mskScore",
            header: t('reports.detail.table.score'),
            sortable: true,
            width: 100,
        },
        {
            key: "healthCategory",
            header: t('reports.detail.table.msk'),
            sortable: true,
            render: row =>
                t(Utils.getHealthRiskTranslationReference(row.healthCategory)),
            sortAccessor: row =>
                HEALTH_SEVERITY_ORDER[row.healthCategory ?? ""] ?? 999,
            width: 100,
        },
        {
            key: "inPain",
            header: t('reports.detail.table.pain'),
            sortable: true,
            render: row =>
                row.inPain
                    ? <span className="text-red-dark">{t('reports.detail.table.inPain.yes')}</span>
                    : t('reports.detail.table.inPain.no'),
            sortAccessor: row => Number(row.inPain),
            width: 60,
        },
        {
            key: "focusArea1",
            header: t('reports.detail.table.focusArea1'),
            sortable: true,
            render: row =>
                row.focusAreas?.[0]
                    ? t(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(row.focusAreas[0]))
                    : "",
            sortAccessor: row => row.focusAreas?.[0] ?? "",
            width: 170,
        },
        {
            key: "focusArea2",
            header: t('reports.detail.table.focusArea2'),
            sortable: true,
            render: row =>
                row.focusAreas?.[1]
                    ? t(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(row.focusAreas[1]))
                    : "",
            sortAccessor: row => row.focusAreas?.[1] ?? "",
            width: 170,
        },
    ], [t]);

    const filterDefinitions: ReportFilterDefinition[] = useMemo(() => [
        {
            key: "groupIds",
            label: t('reports.filterOptions.groups'),
            placeholder: t('reports.filterOptions.allGroups'),
            type: ReportFilterType.MultiSelect,
            options: groups.map(g => ({ label: g.name, value: g.id }))
        },
        {
            key: "tagIds",
            label: t('reports.filterOptions.tags'),
            placeholder: t('reports.filterOptions.allTags'),
            type: ReportFilterType.MultiSelect,
            options: tags.map(t => ({ label: t.name, value: t.id }))
        },
        {
            key: "healthCategories",
            label: t('reports.filterOptions.healthCategories'),
            placeholder: t('reports.filterOptions.allCategories'),
            type: ReportFilterType.MultiSelect,
            options: MskHealthRiskOptions
        },
        {
            key: "focusAreas",
            label: t('reports.filterOptions.focusAreas'),
            placeholder: t('reports.filterOptions.allFocusAreas'),
            type: ReportFilterType.MultiSelect,
            options: FocusGroupOptions
        },
        {
            key: "inPain",
            label: t("reports.filterOptions.pain"),
            type: ReportFilterType.SingleSelect,
            options: painOptions
        }
    ], [groups, tags, painOptions, t]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        const load = async () => {
            const { groups, tags } = await ReportDataUtil.loadGroupsAndTags(orgId);
            setGroups(groups);
            setTags(tags);
        };

        load();
    }, [state.organization?.id]);

    useEffect(() => {
        if (!reportData) return;
        const filtered = applyFilters(reportData, filters, searchQuery);
        setFilteredReportData(filtered);

    }, [filters, reportData, searchQuery]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId || !selectedReportMonth) return;

        // Clear existing data immediately
        setReportData(null);
        setFilteredReportData(null);

        // reset UI state
        setFilters(DEFAULT_FILTERS);
        setSearchQuery("");

        const loadData = async () => {

            const data = await ReportDataUtil.grabInitialDetailData(orgId, selectedReportMonth);
            setReportData(data);
            setFilteredReportData(data);
        };
        loadData();
    }, [selectedReportMonth, state.organization?.id]);

    useEffect(() => {
        if (reportData) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
        }
    }, [reportData])

    const applyFilters = (
        reportData: ReportDynamicDetailData,
        filters: DetailFilterState,
        searchQuery: string
    ): ReportDynamicDetailData => {

        const filteredUsers = reportData.users.filter(user => {

            if (
                searchQuery &&
                !user.name.toLowerCase().includes(searchQuery.toLowerCase())
            ) {
                return false;
            }

            if (
                filters.groupIds.length > 0 &&
                (!user.groupId || !filters.groupIds.includes(user.groupId))
            ) {
                return false;
            }

            if (
                filters.tagIds.length > 0 &&
                !user.tags?.some(tag => filters.tagIds.includes(tag.id))
            ) {
                return false;
            }

            if (
                filters.healthCategories.length > 0 &&
                (!user.healthCategory ||
                    !filters.healthCategories.includes(user.healthCategory))
            ) {
                return false;
            }

            if (filters.inPain === PainFilterOption.YES && !user.inPain) {
                return false;
            }

            if (filters.inPain === PainFilterOption.NO && user.inPain) {
                return false;
            }

            if (filters.focusAreas.length > 0) {
                const expandedFilters = GroupUtil.expandFilterFocusAreas(filters.focusAreas);
                if (!user.focusAreas?.some(f => expandedFilters.includes(f))) {
                    return false;
                }
            }

            return true;
        });

        const counts = ReportDataUtil.calculateDetailCounts(filteredUsers);

        return {
            users: filteredUsers,
            reportDate: reportData.reportDate,
            organizationName: reportData.organizationName,
            totalUsers: filteredUsers.length,
            ...counts
        };
    };
    const handleExportCsv = () => {
        if (!filteredReportData?.users) return;

        const csvString = ReportDataUtil.generateDetail(filteredReportData.users);

        const blob = new Blob(
            ["\uFEFF" + csvString], // UTF-8 BOM for Excel
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${t('reports.detail.fileReportName')}_${selectedReportMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />
            <div className={`${COMPONENT_CLASS}__header-container`}>
                <div className={`${COMPONENT_CLASS}__header`}>
                    <h1>{t('reportType.detail_rep')}</h1>
                    <p className={`label-large ${COMPONENT_CLASS}__sub-header`}>{filteredReportData?.organizationName} • {selectedReportMonth} • {filteredReportData?.users.length} {t('general.users')}</p>
                </div>
                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button
                        onClick={handleExportCsv}
                        buttonText={t('buttons.btn_exportCSV')}
                        type="link" />
                    <Button
                        disabled={!filteredReportData?.users}
                        onClick={() => ReportDataUtil.downloadDetailOrScore(filteredReportData?.users!, ReportDataUtil.buildReportDetailFilterPayload(groups, tags, filters), state.organization?.id!, filteredReportData?.organizationName ?? '', state.user?.id!, selectedReportMonth!, ReportType.Detail)}
                        buttonText={t('buttons.btn_exportPDF')}
                        type="link" />
                    <ReportFilterMenu
                        filters={filterDefinitions}
                        values={filters}
                        onChange={(key, value) =>
                            setFilters(prev => ({
                                ...prev,
                                [key]: value
                            }))
                        }
                    >
                        <label className={`${COMPONENT_CLASS}__label body-bold`}>
                            {t('reports.filterOptions.reportDate')}
                        </label>
                        <Select
                            name="dateEnd"
                            classNamePrefix={COMPONENT_CLASS}
                            placeholder={t('reports.filterOptions.reportDate')}
                            options={reportMonthOptions}
                            value={reportMonthOptions.find(o => o.value === selectedReportMonth)}
                            onChange={(option: any) => {
                                if (!option) return;
                                setSelectedReportMonth(option.value);
                            }}
                        />
                    </ReportFilterMenu>
                </div>
            </div>

            <div className={`${COMPONENT_CLASS}__summary`}>
                <div className={`${COMPONENT_CLASS}__summary-items`}>
                    <div className={`${COMPONENT_CLASS}__summary-item bg-red-light`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.highRiskCount}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.movement.risk.high')}</span>
                    </div>

                    <div className={`${COMPONENT_CLASS}__summary-item bg-yellow-light`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.moderateRiskCount}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.movement.risk.moderate')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item bg-blue-light`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.slightRiskCount}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.movement.risk.slight')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item bg-green-light`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.lowRiskCount}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.movement.risk.low')}</span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item bg-gray-blue`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.usersWithPainCount}</span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.detail.usersWithPain')}</span>
                    </div>
                </div>
            </div>

            {filteredReportData?.users && (
                <div className={`${COMPONENT_CLASS}__table-container`}>
                    <div className={`${COMPONENT_CLASS}__table-header`}>
                        <h2>{t('reports.individualScores')}</h2>
                        <div className={`${COMPONENT_CLASS}__search-container`}>
                            <input
                                className={`input-field ${COMPONENT_CLASS}__search-input`}
                                type="text"
                                placeholder={t('reports.filterOptions.searchByName')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <ReportDynamicTable
                        rows={filteredReportData?.users}
                        columns={columns}
                        pageSize={25}
                    />
                </div>
            )

            }

        </div>
    );
}


export default ReportDetailDynamic;
